"use client";

import type { MutationOptions } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import gql from "graphql-tag";
import type {
  CancelCanvasTriageAppointmentInput,
  CanvasSchedulerResponse,
} from "@rmh/defs";
import type { GeneratedMutation } from "@aws-amplify/api-graphql";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";

interface CancelCanvasTriageAppointmentVariables {
  input: CancelCanvasTriageAppointmentInput;
}

interface CancelCanvasTriageAppointmentResponse {
  data?: {
    cancelCanvasTriageAppointment: CanvasSchedulerResponse;
  };
}

export const cancelCanvasTriageAppointmentMutation = gql`
  mutation cancelCanvasTriageAppointment(
    $input: CancelCanvasTriageAppointmentInput!
  ) {
    cancelCanvasTriageAppointment(input: $input) {
      success
      error {
        type
        message
        details
      }
      data {
        appointmentId
        canvasAppointmentId
        canvasPractitionerId
        appointmentDateTime
        timestamp
      }
    }
  }
`.loc?.source.body as GeneratedMutation<
  CancelCanvasTriageAppointmentVariables,
  {
    cancelCanvasTriageAppointment: CanvasSchedulerResponse;
  }
>;

export const useCancelCanvasTriageAppointment = (
  options: MutationOptions<
    CancelCanvasTriageAppointmentResponse,
    Error,
    CancelCanvasTriageAppointmentVariables
  > = {}
) => {
  const { mutateAsync, isPending } = useMutation<
    CancelCanvasTriageAppointmentResponse,
    Error,
    CancelCanvasTriageAppointmentVariables
  >({
    ...options,
    mutationFn: async (variables) =>
      fetchGraphQLClient(cancelCanvasTriageAppointmentMutation, variables),
  });

  const cancelCanvasTriageAppointment = async (
    input: CancelCanvasTriageAppointmentInput
  ) => {
    try {
      const response = await mutateAsync({ input });

      return response?.data?.cancelCanvasTriageAppointment;
    } catch (error) {
      console.error("Failed to cancel Canvas triage appointment", error);
      throw error;
    }
  };

  return {
    cancelCanvasTriageAppointment,
    isPending,
  };
};
